import React, { useState, useEffect } from "react";
import {
  PiFolderSimple,
  PiCaretLeft,
  PiCaretRight,
  PiClock,
} from "react-icons/pi";
import axios from "axios";
import Categories from "../components/Categories";
import { env } from "../config";
import dayjs from "dayjs";

import CmsPage from "./CmsPage";

function getWeekNumber(date) {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const firstDayOfWeek = firstDay.getDay();
  const offset = (firstDayOfWeek + 6) % 7;
  const dayOfMonth = date.getDate();
  return Math.ceil((dayOfMonth + offset) / 7);
}

function getTotalWeeks(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const offset = (firstDay.getDay() + 6) % 7;
  return Math.ceil((lastDay.getDate() + offset) / 7);
}

const ThisWeek = () => {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categoryWeeks, setCategoryWeeks] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategoriesAndEvents = async () => {
      try {
        setLoading(true);
        const catRes = await axios.get(`${env.baseUrl}/api/categories/events`);
        const catList = catRes.data.categories || [];
        setCategories(catList);

        if (catList.length > 0) {
          setActiveCategory(catList[0].slugurl);
        }

        const initialWeeks = {};
        catList.forEach((cat) => {
          initialWeeks[cat.slugurl] = {
            currentDate: new Date(),
            events: [],
            loading: false,
          };
        });
        setCategoryWeeks(initialWeeks);

        catList.forEach((cat) => {
          fetchCategoryEvents(cat.slugurl);
        });
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoriesAndEvents();
  }, []);

  const fetchCategoryEvents = async (categorySlug) => {
    try {
      setCategoryWeeks((prev) => ({
        ...prev,
        [categorySlug]: { ...prev[categorySlug], loading: true },
      }));

      const res = await axios.get(`${env.baseUrl}/api/events/${categorySlug}?page=1`);
      const allEvents = res.data?.posts?.data || [];

      setCategoryWeeks((prev) => ({
        ...prev,
        [categorySlug]: {
          ...prev[categorySlug],
          events: allEvents,
          loading: false,
        },
      }));
    } catch (error) {
      console.error(`Error fetching events for ${categorySlug}:`, error);
      setCategoryWeeks((prev) => ({
        ...prev,
        [categorySlug]: { ...prev[categorySlug], loading: false },
      }));
    }
  };

  const handlePrevMonth = (categorySlug) => {
    setCategoryWeeks((prev) => {
      const current = prev[categorySlug]?.currentDate || new Date();
      const newDate = new Date(current.getFullYear(), current.getMonth() - 1, 1);
      return {
        ...prev,
        [categorySlug]: { ...prev[categorySlug], currentDate: newDate },
      };
    });
  };

  const handleNextMonth = (categorySlug) => {
    setCategoryWeeks((prev) => {
      const current = prev[categorySlug]?.currentDate || new Date();
      const newDate = new Date(current.getFullYear(), current.getMonth() + 1, 1);
      return {
        ...prev,
        [categorySlug]: { ...prev[categorySlug], currentDate: newDate },
      };
    });
  };

  const handlePrevWeek = (categorySlug) => {
    setCategoryWeeks((prev) => {
      const current = prev[categorySlug]?.currentDate || new Date();
      const newDate = new Date(current);
      newDate.setDate(newDate.getDate() - 7);
      return {
        ...prev,
        [categorySlug]: { ...prev[categorySlug], currentDate: newDate },
      };
    });
  };

  const handleNextWeek = (categorySlug) => {
    setCategoryWeeks((prev) => {
      const current = prev[categorySlug]?.currentDate || new Date();
      const newDate = new Date(current);
      newDate.setDate(newDate.getDate() + 7);
      return {
        ...prev,
        [categorySlug]: { ...prev[categorySlug], currentDate: newDate },
      };
    });
  };

  const getWeekDates = (date) => {
    const d = new Date(date);
    const day = (d.getDay() + 6) % 7;
    const startOfWeek = new Date(d);
    startOfWeek.setDate(d.getDate() - day);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const nextDay = new Date(startOfWeek);
      nextDay.setDate(startOfWeek.getDate() + i);
      weekDays.push(nextDay);
    }
    return weekDays;
  };

  const isEventInWeek = (eventDateStr, weekDates) => {
    if (!eventDateStr) return false;
    const eventDate = new Date(eventDateStr);
    const startOfWeek = new Date(weekDates[0]);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(weekDates[6]);
    endOfWeek.setHours(23, 59, 59, 999);

    return eventDate >= startOfWeek && eventDate <= endOfWeek;
  };

  const isEventExpired = (eventDateStr) => {
    if (!eventDateStr) return false;
    const eventDate = new Date(eventDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  };

  const renderCategoryCalendar = (category) => {
    const catData = categoryWeeks[category.slugurl] || {
      currentDate: new Date(),
      events: [],
      loading: false,
    };

    const currentDate = catData.currentDate;
    const weekDates = getWeekDates(currentDate);
    const weekNumber = getWeekNumber(currentDate);
    const totalWeeks = getTotalWeeks(currentDate.getFullYear(), currentDate.getMonth());

    const monthName = currentDate.toLocaleString("default", { month: "long" });
    const year = currentDate.getFullYear();

    const weekEvents = (catData.events || []).filter((event) =>
      isEventInWeek(event.start_date, weekDates)
    );

    return (
      <div key={category.id} className="category-calendar-block mb-5">
        <div className="category-header d-flex align-items-center mb-3">
          <h3 className="category-title" style={{ margin: 0 }}>
            {category.name}
          </h3>
        </div>

        <div className="calendar-box">
          <div className="calendar-header d-flex justify-content-between align-items-center">
            <button
              type="button"
              className="nav-arrow prev"
              onClick={() => handlePrevMonth(category.slugurl)}
            >
              ‹
            </button>
            <div className="calendar-month">
              {year} {monthName}
            </div>
            <button
              type="button"
              className="nav-arrow next"
              onClick={() => handleNextMonth(category.slugurl)}
            >
              ›
            </button>
          </div>

          <div className="week-navigation d-flex justify-content-between align-items-center">
            <button
              type="button"
              className="nav-arrow"
              onClick={() => handlePrevWeek(category.slugurl)}
            >
              ‹
            </button>
            <span className="week-label">
              WEEK {weekNumber} OF {totalWeeks}
            </span>
            <button
              type="button"
              className="nav-arrow"
              onClick={() => handleNextWeek(category.slugurl)}
            >
              ›
            </button>
          </div>

          <div className="week-days">
            {weekDates.map((d, index) => (
              <div key={index} className="week-day">
                <span className="weekday">
                  {d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase()}
                </span>
                <span className="date">{d.getDate()}</span>
              </div>
            ))}
          </div>

          <div className="events-list">
            {catData.loading && (
              <div className="p-3 text-center text-muted">Loading events...</div>
            )}

            {!catData.loading && weekEvents.length === 0 && (
              <div className="no-events">No events this week</div>
            )}

            {!catData.loading &&
              weekEvents.map((post) => {
                const postDate = new Date(post.start_date);
                const expired = isEventExpired(post.start_date);

                return (
                  <div
                    className={`event-row ${expired ? "expired" : ""}`}
                    key={post.id}
                  >
                    <div className="event-date">
                      <span className="day">
                        {String(postDate.getDate()).padStart(2, "0")}
                      </span>
                      <span className="month">
                        {postDate.toLocaleDateString("en-US", { month: "short" })}
                      </span>
                    </div>

                    <div className="event-card">
                      <img src={post.image} alt={post.title} />

                      <div className="event-content">
                        <div className="event-meta">
                          <span className="event-time">
                            <i className="fa-regular fa-clock"></i> All Day
                          </span>
                        </div>

                        <div className="event-title">
                          <a href={`/event/${post.slug}`}>
                            {post.title}
                            {expired && (
                              <span className="expired-tag">Expired</span>
                            )}
                          </a>
                        </div>

                        <div className="event-category">
                          <i className="fa-regular fa-folder"></i> {category.name}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <CmsPage slug="thisweek" titleFallback="This Week">
        <div>
          <section className="thisweek">
            <div className="container">
              <h4>Weekly Events by Category</h4>
              <div id="mec_skin_38523" className="mec-wrap colorskin-custom" style={{ display: "unset" }}>
                {categories.map((category) => renderCategoryCalendar(category))}
              </div>
            </div>
          </section>

          <section className="event-categories">
            <Categories type="events" />
          </section>
        </div>
      </CmsPage>
    </div>
  );
};

export default ThisWeek;
